import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan } from '@features/user';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { Box, Button, Paper, Typography } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

/**
 * Entry card for the evergreen appointment-type catalog (GDN-2335) on the
 * practice appointment types page — sibling to the cleanup wizard's card.
 * Double-gated like the wizard entry: renders nothing unless BOTH the
 * Statsig gate and manage_appointment_types pass. Navigates to the
 * full-height catalog route; the catalog's own `opened` event fires there.
 */
export const TypeCatalogEntry: React.FC = () => {
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_TYPE_CATALOG);
  const canManageAppointmentTypes = useCan('manage_appointment_types');
  const navigate = useNavigate();

  if (!isEnabled || !canManageAppointmentTypes) return null;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Browse the full catalog
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, maxWidth: 680 }}
          >
            See every appointment type across your practice — who owns it, how
            much it&apos;s used, and the note template behind it — and archive
            or restore anything.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<TableChartOutlinedIcon />}
          onClick={(): void => navigate('/practice/appointment-types/catalog')}
          sx={{ justifySelf: { xs: 'stretch', md: 'end' } }}
        >
          Open catalog
        </Button>
      </Box>
    </Paper>
  );
};
